// PanelTimesSheet — change a case's SCHEDULED start and end.
//
// Typed, not dragged. The reasoning, kept because it will be re-litigated: the
// day axis is roughly a minute per pixel, so an eight-pixel trackpad slip is ten
// minutes applied silently to the number that drives the coverage flag and the
// boundary staffing maths; a bar is 31-44 px tall so its resize edge would be a
// 4-6 px target; a wall has no mouse; and case times are a five-minute option
// list rather than a continuous quantity. A discrete schedule wants a discrete
// control.
//
// ⚠️ Every label says SCHEDULED. There is no actual start or end event anywhere
// in this system — `buildTimeline` reads only the scheduled strings — so
// "Ends" would claim knowledge the board does not have.

import React, { useEffect, useMemo, useRef, useState } from "react";
import styles, { C, MONO } from "./panelCard.styles";
import {
  validateCaseTimes,
  timeChangeSummary,
  coverageConsequence,
  stepTime,
} from "../../domain/panelTimes";

function Field({ label, value, onChange, busy }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ width: 46, fontSize: 12, color: C.muted }}>{label}</span>
      <button
        type="button"
        aria-label={`${label}: five minutes earlier`}
        onClick={() => onChange(stepTime(value, -5))}
        style={{ ...styles.act, minWidth: 38, minHeight: 38 }}
        disabled={busy}
      >
        −
      </button>
      <input
        type="time"
        step={300}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label={`Scheduled ${label.toLowerCase()}`}
        style={{
          minHeight: 38,
          fontFamily: MONO,
          fontSize: 13,
          padding: "0 8px",
          border: `1px solid ${C.line}`,
          borderRadius: 7,
        }}
      />
      <button
        type="button"
        aria-label={`${label}: five minutes later`}
        onClick={() => onChange(stepTime(value, 5))}
        style={{ ...styles.act, minWidth: 38, minHeight: 38 }}
        disabled={busy}
      >
        +
      </button>
    </div>
  );
}

export default function PanelTimesSheet({
  lane,
  caseItem,
  otherCases = [],
  departMin = null,
  busy = false,
  error = null,
  onCommit,
  onClose,
}) {
  const [startTime, setStartTime] = useState(caseItem?.startTime || "");
  const [endTime, setEndTime] = useState(caseItem?.endTime || "");
  const headingRef = useRef(null);

  useEffect(() => { if (headingRef.current) headingRef.current.focus(); }, []);

  const check = useMemo(
    () => validateCaseTimes({ startTime, endTime, otherCases }),
    [startTime, endTime, otherCases]
  );
  const summary = useMemo(
    () => timeChangeSummary({
      fromStart: caseItem?.startTime, fromEnd: caseItem?.endTime,
      toStart: startTime, toEnd: endTime,
    }),
    [caseItem, startTime, endTime]
  );
  const consequence = useMemo(
    () => (check.ok ? coverageConsequence({ endTime, departMin }) : null),
    [check.ok, endTime, departMin]
  );

  const unchanged =
    startTime === (caseItem?.startTime || "") && endTime === (caseItem?.endTime || "");
  const canSave = check.ok && !unchanged && !busy;

  return (
    // Sheet container with onKeyDown — the keyboard path, not a mouse-only trap.
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    <section
      onKeyDown={(e) => { if (e.key === "Escape" && !busy) { e.stopPropagation(); onClose(); } }}
      aria-label="Scheduled case times"
      data-testid="times-sheet"
      style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: C.radius, overflow: "hidden" }}
    >
      <header style={{ padding: "10px 12px", borderBottom: `1px solid ${C.line}`, background: "#f8fafc" }}>
        <div style={{ fontSize: 10.5, letterSpacing: "0.1em", textTransform: "uppercase", color: C.mutedSoft }}>
          ROOM · {lane?.roomName}
        </div>
        <h2 ref={headingRef} tabIndex={-1} style={{ margin: 0, fontSize: 15, outline: "none" }}>
          Scheduled case times
        </h2>
      </header>

      {error ? (
        <div style={{ padding: "8px 12px", background: C.alarmBg, borderBottom: `2px solid ${C.alarm}`, color: "#7f1d1d", fontWeight: 700 }}>
          {error}
        </div>
      ) : null}

      <div style={{ padding: 12, display: "grid", gap: 8 }}>
        <Field label="Start" value={startTime} onChange={setStartTime} busy={busy} />
        <Field label="End" value={endTime} onChange={setEndTime} busy={busy} />

        {summary ? (
          <div data-testid="times-summary" style={{ fontFamily: MONO, fontSize: 12, color: C.muted }}>
            {summary}
          </div>
        ) : null}

        {/* Prefixed with an arrow on purpose: !, ∅, ● and ▲ are the alarm
            glyphs, and this is not an alarm. "This CRNA now leaves before the
            case ends" is a true state the board exists to display, so it never
            blocks the save. */}
        {consequence ? (
          <div data-testid="times-consequence" style={{ fontSize: 12, color: C.warn, fontWeight: 700 }}>
            → {consequence.text}
          </div>
        ) : null}

        {!check.ok ? (
          <div data-testid="times-invalid" style={{ fontSize: 12, color: C.alarm, fontWeight: 700 }}>
            {check.error}
          </div>
        ) : null}
      </div>

      <footer style={{ display: "flex", gap: 8, padding: "10px 12px", borderTop: `1px solid ${C.line}` }}>
        <button type="button" onClick={onClose} style={{ ...styles.act, marginLeft: "auto" }}>
          Cancel
        </button>
        <button
          type="button"
          onClick={() => canSave && onCommit({ startTime, endTime })}
          aria-disabled={!canSave ? "true" : undefined}
          style={{
            ...styles.act,
            ...(canSave ? styles.actPrimary : { background: "#94a3b8", borderColor: "#94a3b8", color: "#fff", cursor: "not-allowed" }),
          }}
        >
          {busy ? "Saving…" : "Save times"}
        </button>
      </footer>
    </section>
  );
}
